import { Formik } from 'formik'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import {
  isPaymentProviderBankType,
  isPaymentProviderNoneType,
  isPaymentProviderPaymentAgentType,
} from '@model/PaymentProviderType'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { CURRENCY_TYPE } from '@shared/components/CurrencyAmountChip/CurrencyAmountChip'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import {
  buildWithdrawalInitialValues,
  isRealWithdrawalPspId,
  validateWithdrawalProviderFields,
} from './helpers'
import { useWithdrawalDestination } from './hooks/useWithdrawalDestination'
import { useWithdrawalPaymentProvider } from './hooks/useWithdrawalPaymentProvider'
import { useWithdrawalProviderCache } from './hooks/useWithdrawalProviderCache'
import { useWithdrawalUrlProvider } from './hooks/useWithdrawalUrlProvider'
import type { WithdrawalFlowLocationState, WithdrawalFormValues, WithdrawalTarget } from './types'

interface WithdrawalFlowLayoutPageProps {
  target?: WithdrawalTarget
  targetId?: string
  dashboardContext?: 'traders-room' | 'payment-agent' | 'introducing-broker'
}

export const WithdrawalFlowLayoutPage: React.FC<WithdrawalFlowLayoutPageProps> = (props) => {
  const { target: targetProp, targetId: targetIdProp, dashboardContext = 'traders-room' } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const locale = useSessionLanguage()

  const location = useLocation()
  const locationState = useMemo(
    () => (location.state || {}) as WithdrawalFlowLocationState,
    [location.state]
  )

  const params = useParams<{
    target: WithdrawalTarget
    id: string
    walletId?: string
    pspId?: string
  }>()
  const target = targetProp ?? params.target
  const walletId = targetIdProp ?? params.id ?? params.walletId
  const pspId = params.pspId

  const { walletPaymentProvider, setWalletPaymentProvider, selectionState } =
    useWithdrawalProviderCache({
      target,
      id: walletId,
      pspId,
      locationState,
    })

  const { destination, destinationLoading, walletIdForProvider } = useWithdrawalDestination({
    target,
    id: walletId,
  })

  const { isUrlProviderLoading, urlProviderResolved } = useWithdrawalUrlProvider({
    target,
    id: walletId,
    pspId,
    walletIdForProvider,
    destinationLoading,
    walletPaymentProvider,
    setWalletPaymentProvider,
    locale,
  })

  const handleBack = useCallback(() => {
    if (!target) return

    navigate(`/dashboard/${dashboardContext}/withdrawal/${target}`, { state: selectionState })
  }, [navigate, dashboardContext, selectionState, target])

  const { paymentProvider, providerLoading } = useWithdrawalPaymentProvider({
    walletPaymentProvider,
    walletIdForProvider,
    locale,
    onBack: handleBack,
  })

  const needsUrlProviderResolution = isRealWithdrawalPspId(pspId)
  const isActuallyLoading =
    destinationLoading ||
    providerLoading ||
    isUrlProviderLoading ||
    (needsUrlProviderResolution ? !urlProviderResolved : false)

  const { formatMoney } = useFormatNumber()

  const initialValues = useMemo(() => {
    if (!paymentProvider || !destination || !target) return null

    return buildWithdrawalInitialValues({
      destination,
      paymentProvider,
      dashboardContext,
    })
  }, [destination, paymentProvider, dashboardContext, target])

  if (isActuallyLoading) {
    return <FundingTemplate isLoading onBack={handleBack} />
  }

  if (
    !target ||
    !walletId ||
    !walletPaymentProvider ||
    !destination ||
    !paymentProvider ||
    !initialValues
  ) {
    return null
  }

  return (
    <Formik<WithdrawalFormValues>
      initialValues={initialValues}
      enableReinitialize
      validate={(values) => {
        const errors: any = {}

        const categoryId = values.paymentProvider?.providerCategory?.id
        const providerCurrencyId = values.paymentProvider?.currency?.id

        const setNestedError = (path: string, message: string) => {
          const keys = path.split('.')
          let cur = errors
          for (let i = 0; i < keys.length - 1; i++) {
            const k = keys[i]
            cur[k] = cur[k] || {}
            cur = cur[k]
          }
          cur[keys[keys.length - 1]] = message
        }

        const fieldErrors = validateWithdrawalProviderFields(
          values.paymentProvider?.parameters?.form || [],
          values.fields,
          t
        )
        if (Object.keys(fieldErrors).length > 0) {
          errors.fields = fieldErrors
        }

        if (!isPaymentProviderPaymentAgentType(categoryId)) {
          const rawAmount = values.amount
          const amount =
            rawAmount === null || rawAmount === undefined ? undefined : Number(rawAmount)
          const hasValidAmount = typeof amount === 'number' && Number.isFinite(amount)

          if (amount === undefined || !hasValidAmount) {
            errors.amount = t('Enter an amount')
          } else if (amount <= 0) {
            errors.amount = t('Amount must be greater than zero')
          } else if (amount > values.wallet.balance) {
            errors.amount = t('Insufficient funds')
          } else if (walletPaymentProvider?.minAmount && amount < walletPaymentProvider.minAmount) {
            errors.amount = t('Method minimum is {{amount}}', {
              amount: formatMoney(
                walletPaymentProvider.minAmount,
                providerCurrencyId || CURRENCY_TYPE.USD
              ),
            })
          } else if (walletPaymentProvider?.maxAmount && amount > walletPaymentProvider.maxAmount) {
            errors.amount = t('Method maximum is {{amount}}', {
              amount: formatMoney(
                walletPaymentProvider.maxAmount,
                providerCurrencyId || CURRENCY_TYPE.USD
              ),
            })
          }
        }

        if (!isPaymentProviderNoneType(categoryId)) {
          if (!values.terms?.paymentOperations) {
            setNestedError('terms.paymentOperations', t('Validation.Required'))
          }

          if (isPaymentProviderPaymentAgentType(categoryId)) {
            if (!values.terms?.powerOfAttorney) {
              setNestedError('terms.powerOfAttorney', t('Validation.Required'))
            }
          }
        }

        if (
          isPaymentProviderBankType(categoryId) &&
          values.hasBankAccounts === true &&
          !values.bankAccount?.id
        ) {
          errors.bankAccount = t('Validation.Required')
        }

        if (
          isPaymentProviderBankType(categoryId) &&
          values.hasBankAccounts === false &&
          !values.bankDepositProof?.fileName
        ) {
          errors.bankDepositProof = t('Validation.Required')
        }

        if (values.comment && values.comment.length > 100) {
          errors.comment = t('Validation.The character limit is 100 characters')
        }

        return errors
      }}
      onSubmit={() => undefined}
    >
      <Outlet />
    </Formik>
  )
}
